import React, { useEffect, useRef, useState } from "react";
import { Modal, View, Text, TouchableOpacity, StyleSheet, Platform } from "react-native";
import { useRecoilValue } from "recoil";
import { paymentActiveState, idlePausedState } from "./state/idleState";
import apps from "./lib/apps";
import { PALETTE } from "./lib/palette";
import { bypassNextUnload } from "./lib/nav";

/**
 * Inactivity Timeout(무활동 세션 초기화, ②) — 매장별 옵션(shop.idleReset)이 켜진 경우만 동작.
 * - shop.idleMinutes 분 동안 아무 상호작용이 없으면 경고 모달 표시.
 * - 경고 후 shop.idleWarnSeconds 초 안에도 상호작용이 없으면 세션 초기화(웹: 쿼리 제거 후 리로드 →
 *   splash가 "session expired, scan QR" 안내). 상호작용이 생기면 경고 해제 + 타이머 리셋.
 * 정지 조건(중요):
 * - 카드결제 진행 중(paymentActiveState): UrPay iframe은 cross-origin이라 내부 입력 이벤트가 부모로
 *   버블링되지 않아 활동 감지가 0이 된다 → 상태 기반으로 정지(오발동 방지).
 * - 종료·읽기전용 화면(idlePausedState: Thankyou/OrderReview).
 * App 최상위에 마운트되어 화면 전환과 무관하게 동작한다. Splash 단계에선 currentShop이 없어 비활성.
 */
export default function IdleGuard() {
    const paymentActive = useRecoilValue(paymentActiveState);
    const idlePaused = useRecoilValue(idlePausedState);

    const [warnOpen, setWarnOpen] = useState(false);
    const [remain, setRemain] = useState(0);

    const lastActivity = useRef(Date.now());
    const warnOpenRef = useRef(false);
    const pausedRef = useRef(false);
    const evaluateRef = useRef<() => void>(() => {});

    useEffect(() => { pausedRef.current = paymentActive || idlePaused; }, [paymentActive, idlePaused]);

    // 판정은 전부 "벽시계(Date.now)" 기준으로 계산한다 → setInterval이 화면잠금/절전/백그라운드에서
    // 스로틀·정지돼도, 다음 실행(틱 또는 포그라운드 복귀) 시 실제 경과시간이 그대로 반영된다.
    // 경고 윈도우는 마지막 활동 시각으로부터 파생: [lastActivity+idle, +warn]. 잠금 동안 이 구간이
    // 통째로 지났으면 깨어날 때 바로 초기화된다.
    evaluateRef.current = () => {
        const shop = apps.currentShop;
        const enabled = shop?.idleReset === true;
        if (!enabled || pausedRef.current) {
            if (warnOpenRef.current) { warnOpenRef.current = false; setWarnOpen(false); }
            lastActivity.current = Date.now();   // 정지 중엔 idle 누적 방지(재개 시 새로 카운트)
            return;
        }
        const idleMs = (shop?.idleMinutes ?? 5) * 60 * 1000;
        const warnMs = (shop?.idleWarnSeconds ?? 60) * 1000;
        const now = Date.now();
        const warnStart = lastActivity.current + idleMs;   // 경고 시작(벽시계)
        const warnEnd = warnStart + warnMs;                // 초기화 시각(벽시계)

        if (now >= warnEnd) {
            warnOpenRef.current = false;
            setWarnOpen(false);
            resetSession();
        } else if (now >= warnStart) {
            if (!warnOpenRef.current) { warnOpenRef.current = true; setWarnOpen(true); }
            setRemain(Math.ceil((warnEnd - now) / 1000));
        } else {
            if (warnOpenRef.current) { warnOpenRef.current = false; setWarnOpen(false); }
        }
    };

    // 활동 감지: 경고창이 떠 있는 동안엔 무시한다 → 오직 "I'm still here" 버튼으로만 해제(오탭 방지).
    // 그 외(경고 전)엔 마지막 활동 시각을 갱신해 타이머를 리셋한다.
    useEffect(() => {
        if (Platform.OS !== "web" || typeof window === "undefined") return;
        const onActivity = () => {
            if (warnOpenRef.current) return;
            lastActivity.current = Date.now();
        };
        const evs = ["pointerdown", "keydown", "touchstart", "wheel", "scroll", "mousemove"];
        const opts: any = { passive: true, capture: true };
        evs.forEach(e => window.addEventListener(e, onActivity, opts));
        return () => evs.forEach(e => window.removeEventListener(e, onActivity, opts));
    }, []);

    // 1초 틱
    useEffect(() => {
        const id = setInterval(() => evaluateRef.current(), 1000);
        return () => clearInterval(id);
    }, []);

    // 화면잠금/절전에서 깨어나 포그라운드로 돌아오면 즉시 재평가(스로틀로 놓친 경과시간 즉시 반영).
    // 복귀 자체는 "활동"으로 치지 않는다 → 잠금 동안 흐른 시간도 무활동으로 계속 카운트된다.
    useEffect(() => {
        if (Platform.OS !== "web" || typeof window === "undefined") return;
        const onWake = () => { if (document.visibilityState === "visible") evaluateRef.current(); };
        document.addEventListener("visibilitychange", onWake);
        window.addEventListener("pageshow", onWake);
        window.addEventListener("focus", onWake);
        return () => {
            document.removeEventListener("visibilitychange", onWake);
            window.removeEventListener("pageshow", onWake);
            window.removeEventListener("focus", onWake);
        };
    }, []);

    // 버튼(명시적)으로만 해제 — 타이머 리셋.
    const onStay = () => {
        lastActivity.current = Date.now();
        warnOpenRef.current = false;
        setWarnOpen(false);
    };

    const accent = safeAccent();

    // 버튼으로만 해제되는 모달: onRequestClose(안드로이드 back)도 무시, 배경 탭도 해제 안 함.
    return (
        <Modal visible={warnOpen} transparent animationType="fade" onRequestClose={() => {}}>
            <View style={styles.backdrop}>
                <View style={styles.card}>
                    <Text style={styles.title}>Are you still there?</Text>
                    <Text style={styles.msg}>Your order session will reset in</Text>
                    <Text style={[styles.count, { color: accent }]}>{remain}s</Text>
                    <TouchableOpacity onPress={onStay} style={[styles.btn, { backgroundColor: accent }]}>
                        <Text style={styles.btnText}>I'm still here</Text>
                    </TouchableOpacity>
                </View>
            </View>
        </Modal>
    );
}

function safeAccent(): string {
    try { return apps.getAccent(); } catch (e) { return PALETTE.ACCENT; }
}

// 세션 초기화: 웹은 QR 파라미터(쿼리) 제거 후 전체 리로드 → 모든 in-memory 상태 소멸 +
// splash가 "Your session has expired. Please scan the table QR code again" 안내.
function resetSession() {
    if (typeof window !== "undefined" && window.location) {
        bypassNextUnload();   // 무활동 리셋의 프로그램적 이탈은 LeaveGuard 다이얼로그 없이 통과
        const clean = window.location.origin + window.location.pathname;
        window.location.replace(clean);
    }
}

const styles = StyleSheet.create({
    backdrop: { flex: 1, backgroundColor: "rgba(20,24,32,0.45)", alignItems: "center", justifyContent: "center", padding: 24 },
    card: { width: "100%", maxWidth: 340, backgroundColor: PALETTE.SURFACE, borderRadius: 18, paddingVertical: 26, paddingHorizontal: 22, alignItems: "center" },
    title: { fontSize: 20, fontWeight: "900", color: PALETTE.TEXT, letterSpacing: -0.4 },
    msg: { marginTop: 12, fontSize: 14, fontWeight: "700", color: PALETTE.MUTED },
    count: { marginTop: 4, fontSize: 40, fontWeight: "900", letterSpacing: -1 },
    btn: { marginTop: 20, alignSelf: "stretch", borderRadius: PALETTE.RADIUS_PILL, paddingVertical: 14, alignItems: "center" },
    btnText: { color: "#fff", fontSize: 16, fontWeight: "900", letterSpacing: -0.3 },
});
